// Pure presentation + optimistic helpers for the messaging inbox. Kept OUT of the React hooks
// so they are unit-testable in isolation (mirrors useGuestList's guestSeats/sumSeats split). NO device-local
// timestamp ever renders a trailing `Z` (the MVP's UTC-leak defect); message times are 12h wall-clock.
import type { components } from "@cntrl/api-client";
import { crewPartyLabel } from "../../features/contacts/crewParty";

type ReactionDto = components["schemas"]["ReactionDto"];
type ThreadSummary = components["schemas"]["ThreadSummary"];

/** The reaction palette the picker offers (presentation vocabulary only — no server enum). */
export const REACTION_EMOJIS = ["👍", "❤️", "🔥", "😂", "🎉"] as const;

/** Up to two initials from a display name for an avatar placeholder (born-blank → "?"). Media→URL is Phase 4. */
export function nameInitials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  return initials || "?";
}

/**
 * The row/header label for a thread. A DIRECT thread is named for the OTHER person from the served
 * directory; a SHOW thread for its show (and, on Pro, its party). The band kind is retired (ART-159), so
 * there is no third case.
 *
 * **A Pro band's show threads are named by their PARTY (ART-145), and that is not decoration.** One show used
 * to mean one chat; it now means up to five, and without the party every one of them reads the same literal
 * "Show thread" — a column of identical rows the reader cannot tell apart, which is the failure
 * `TrendingItem.Subject` and the acting-pill column were each written up for. The party is the ONE thing that
 * distinguishes them, so it is the one thing the label says. A Standard band's thread carries no party and is
 * unchanged.
 *
 * `crewPartyLabel` is imported rather than re-spelt: the five labels are a vocabulary the crew list already
 * owns, and a second copy here is what drifts the day the PO renames one (they were "Teams" until 2026-09-10).
 */
export function threadTitle(
  t: Pick<ThreadSummary, "kind" | "title" | "crewParty" | "show" | "tour" | "participantPersonIds">,
  /** The payload's person directory + who the viewer is, so a DM can name the OTHER person. */
  ctx?: { names?: ReadonlyMap<string, string>; myPersonId?: string | null },
): string {
  // A NAME SOMEBODY CHOSE WINS (ART-159), and it is resolved here rather than at each surface so the list
  // row, the header and the edit dialog's placeholder cannot disagree about what this chat is called.
  // A DM is the exception and never consults it: its name is the other person's, which is theirs to change
  // and not the reader's — so the server refuses a title on one and this never looks for it.
  if (t.kind === "direct") return directTitle(t, ctx);
  const chosen = t.title?.trim();
  if (chosen) return chosen;
  // A TOUR chat is named for the RUN, not for any one of its dates (ART-159). The tour has a real name
  // column, so this is the one chat whose default needs no composing at all — where a show has no title and
  // `showThreadLabel` has to pick a place out of venue / city / festival / date.
  if (t.kind === "show") return t.tour?.name?.trim() || showThreadLabel(t.show) || "Show thread";
  // A GROUP with no chosen name is named for its MEMBERS, excluding the reader (PO, 2026-09-13): so "Sam,
  // John, Edward" reads "John and Edward" to Sam. The name is theirs to set later; until then the roster IS
  // the name. Per-viewer by construction (it drops whoever is looking), which is exactly why it is composed
  // here and not stored. `Intl.ListFormat` gives the locale-correct "A and B" / "A, B, and C".
  if (t.kind === "group") {
    const members = (t.participantPersonIds ?? [])
      .filter((id): id is string => !!id && id !== ctx?.myPersonId)
      .map((id) => ctx?.names?.get(id))
      .filter((n): n is string => !!n);
    if (members.length > 0) return groupNameFormat.format(members);
    return "Group chat"; // the directory could not name anyone — the designed neutral, never a raw kind
  }
  return "Conversation";   // unreachable on the wire — the band kind is retired — but never a raw kind string
}

/** "A and B" for two, "A, B, and C" for more — the conjunction list a reader expects for an unnamed group. */
const groupNameFormat = new Intl.ListFormat("en", { style: "long", type: "conjunction" });

/**
 * The two things a chat's tags can say: what KIND it is, and which PARTY it is for. Either may be null.
 *
 * **ONE derivation, both surfaces render both** (PO, 2026-09-11) — up to two tags, "(SHOW) (A PARTY)", in the
 * sidebar row AND in the chat header. They carry what the NAME used to: the name is the venue now, so
 * without them a flat list of "Bluebird" rows has nothing to tell one show's five party chats apart. A shape
 * rather than two functions, so a TOUR or BUS kind lands in one place and both surfaces inherit it.
 *
 * The two tones are not decoration: the kind is the accent chip and the party is the teal one, because a row
 * carrying BOTH in crimson reads as one control rather than as two separate facts.
 *
 * **SERVED, not derived** — the handoff's own instruction, "ship it as a `tag` field on every thread row so
 * clients never re-derive it from string prefixes". Two earlier versions of this note argued the derivation
 * was safe here because ours read structured fields rather than parsing a `show_id` string, and that was
 * true: it cost one ternary when tour chats arrived. What moved it anyway is the SECOND CLIENT. Mobile
 * renders this same row, and a classification implemented twice is two chances to disagree the day a kind is
 * added — which bus chats (ART-160) will be.
 *
 * So this function now RENDERS a verdict instead of reaching one. The split is deliberate: the server owns
 * which bucket a row is in, the client owns what the bucket is called, because a label belongs beside the
 * party labels it sits next to rather than in a migration.
 */
export function threadTags(
  t: Pick<ThreadSummary, "crewParty" | "tag">,
): { kind: string | null; kindTone: string; party: string | null } {
  return {
    // The SERVED classification, turned into words HERE. The server says which bucket the row is in and the
    // client says what that bucket is called — the same split `MessageablePerson.reason` already makes.
    kind: t.tag ? (TAG_LABELS[t.tag] ?? null) : null,
    // The kind chip's TONE. A bus chat is amber (ART-160), everything else keeps the crimson show tone (tour
    // has no distinct tone minted). Derived here, in one place, rather than each component branching on `tag`.
    kindTone: t.tag === "bus" ? "bus" : "show",
    // `crewPartyLabel` is imported rather than re-spelt: the five labels are a vocabulary the crew list
    // already owns, and a second copy is what drifts the day the PO renames one.
    party: crewPartyLabel(t.crewParty) || null,
  };
}

/**
 * The served tag keys, in English. An UNKNOWN key renders NOTHING rather than itself: this vocabulary grows
 * server-side (bus chats are next), and a client one version behind would otherwise put the raw word "bus"
 * in a chip — the `TrendingItem.Subject` failure, where a dedupe key reached a surface as a label.
 */
const TAG_LABELS: Record<string, string> = {
  show: "Show",
  tour: "Tour",
  bus: "Bus",
};

/**
 * WHAT A ROW SAYS THE LAST MESSAGE WAS — one rule, because two clients render this row.
 *
 * Three things compose here and each was its own gap:
 *
 * **The ATTACHMENT (C-Attach).** A picture-only message has no text to preview, so the server sends the
 * CLASSIFICATION (`lastAttachmentKind`) and the file's name, and the WORDS are composed here. That split is
 * the C-Tag rule: baking "📷 Photo" into a server field would put a copy change behind a deploy and give
 * mobile a string it cannot localise. Text WINS when there is text — an attachment sent with a sentence
 * previews the sentence, which is the case a server-composed preview would have got wrong invisibly.
 *
 * **The SENDER PREFIX (C-Prefix).** "Ryan: doors at 7" on a chat with more than two people in it, because
 * "doors at 7" alone tells you nothing about who is asking. Suppressed on a DM, where there is exactly one
 * other person and the row is already named after them — a prefix there is the row's own title repeated
 * inside itself.
 *
 * **YOU.** Your own last message reads "You: …", not your name. Everywhere else in this app the viewer is
 * "You"; a row that called you by name would be the only place it does not.
 */
export function threadPreview(
  t: Pick<ThreadSummary, "kind" | "lastPreview" | "lastSenderPersonId" | "lastAttachmentKind" | "lastAttachmentName">,
  ctx?: { names?: ReadonlyMap<string, string>; myPersonId?: string | null },
): string {
  const body = attachmentPreview(t) ?? t.lastPreview?.trim() ?? "";
  if (!body) return "";
  // A DM names the other person in its TITLE, so prefixing the preview repeats it one line down. Only the
  // multi-person kinds carry a prefix, and the kind is what decides — never the participant count, which
  // is 2 for a two-person group chat that still needs to say who spoke.
  if (t.kind === "direct") return body;

  const sender = t.lastSenderPersonId;
  if (!sender) return body;
  const who = sender === ctx?.myPersonId ? "You" : ctx?.names?.get(sender);
  return who ? `${who}: ${body}` : body;
}

/**
 * The words for a message whose whole content is a file, or null when the last message had text (the text
 * wins) or no attachment at all.
 *
 * An UNKNOWN kind renders NOTHING rather than itself — the `TrendingItem.Subject` rule, and this vocabulary
 * grows server-side exactly as the tag one does.
 */
function attachmentPreview(
  t: Pick<ThreadSummary, "lastPreview" | "lastAttachmentKind" | "lastAttachmentName">,
): string | null {
  if (t.lastPreview?.trim()) return null;
  if (t.lastAttachmentKind === "image") return "📷 Photo";
  if (t.lastAttachmentKind === "file") return `📎 ${t.lastAttachmentName?.trim() || "File"}`;
  return null;
}

/**
 * A HUMAN SIZE for an attachment ("312 KB", "1.4 MB"). Bytes in, a label out; blank for nothing, so a
 * caller renders no dot rather than "0 B".
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * The OTHER people in a direct thread — everyone but the viewer.
 *
 * One authority, because two surfaces answer this about the same row: the title names them and the avatar
 * pictures them. Deriving it twice is how a conversation comes to read "Alex Mercer" beside somebody else's
 * face, which is the quietest possible version of being wrong.
 *
 * Returns a LIST, though a direct thread is two people by construction (`core.create_thread` inserts exactly
 * the pair) — so a future group DM degrades to "all of them" at every call site rather than silently naming
 * and picturing whichever happened to be first.
 */
export function directCounterpartIds(
  t: Pick<ThreadSummary, "participantPersonIds">,
  myPersonId?: string | null,
): string[] {
  return (t.participantPersonIds ?? []).filter((id): id is string => !!id && id !== myPersonId);
}

/**
 * A DM is named for the OTHER person in it.
 *
 * It used to be the literal "Direct message" for every one of them, which was survivable while the only way
 * to start a conversation was band-to-band and DMs barely existed. Once "+ New" is a person picker it is
 * not: someone who can write to six people gets six identical rows (PO, 2026-09-11).
 *
 * The name comes from the DIRECTORY the payload serves, never a second lookup — `IPersonDirectory` owns the
 * precedence (display name → first+last → email) for every surface that names somebody. Falls back to the
 * old literal for a participant the directory could not resolve, which is the designed neutral rather than a
 * raw uuid.
 */
function directTitle(
  t: Pick<ThreadSummary, "participantPersonIds">,
  ctx?: { names?: ReadonlyMap<string, string>; myPersonId?: string | null },
): string {
  const others = directCounterpartIds(t, ctx?.myPersonId);
  const named = others.map((id) => ctx?.names?.get(id)).filter((n): n is string => !!n);
  if (named.length === 0) return "Direct message";
  // A direct thread is two people by construction (core.create_thread inserts exactly the pair), so this is
  // one name in practice — joined rather than [0] so a future group DM degrades to a list instead of
  // silently naming one participant and hiding the rest.
  return named.join(", ");
}

/**
 * What a SHOW is called. `artist.shows` has no title column — only kind, date, venue and city — so the name
 * is something we WRITE, and this is the one place that writes it: the row, the conversation header and
 * anything that names a show thread later all read the same sentence.
 *
 * Venue first, because that is what a crew calls a date ("we're at the Fillmore Thursday"), with the city as
 * the disambiguator when a venue name is generic and the date when it is not. A festival says so instead of
 * naming a venue it may not have.
 *
 * **Every part is optional and the fallbacks matter.** A show entered as nothing but a date is a real,
 * ordinary row — the booking flow creates holds with no venue — so this degrades to the date alone rather
 * than to an empty string that would send the caller to "Show thread".
 */
export function showThreadLabel(show: ThreadSummary["show"] | null | undefined): string {
  if (!show) return "";
  const place = show.venueName?.trim() || show.city?.trim() || (show.kind === "festival" ? "Festival" : "");
  // The DATE is a last resort, not a suffix (PO, 2026-09-11). It used to be appended to every name and the
  // party after it — "Bluebird [test data] · Oct 1 · A Party" — which made the name carry three facts and
  // left the truncation to decide which one a reader got. The date is its own slot in the header now and
  // the party is a tag, so the name is the PLACE. A show with neither a venue nor a city still has to say
  // something, and the date is the only thing left that identifies it.
  return place || formatShowDate(show.date);
}

/** A show's date for the header slot beside the crew — the same rendering the name used to end with. */
export function showThreadDate(show: ThreadSummary["show"] | null | undefined): string {
  return show ? formatShowDate(show.date) : "";
}

/** A show's date as a crew reads it off a routing sheet — "Nov 14", and the year only when it is not this one. */
function formatShowDate(iso: string | null | undefined): string {
  if (!iso) return "";
  // A date-only wire value ("2026-11-14") parses as UTC MIDNIGHT, which renders as the 13th anywhere west of
  // Greenwich. Split it rather than letting Date guess — the show is a calendar DAY, not an instant, the
  // same distinction offerGoodThrough and holdCountdown are built around.
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  const local = new Date(y, m - 1, d);
  if (Number.isNaN(local.getTime())) return "";
  const sameYear = y === new Date().getFullYear();
  return local.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/**
 * How long a run of messages from ONE sender keeps sharing a single header before the header comes back.
 * Measured from the FIRST message of the run, not the previous one — so a sender typing steadily for half an
 * hour gets a fresh header every ten minutes rather than none at all, which is what a gap-since-previous rule
 * would give. A business value in the config-rows sense, but it is pure presentation and belongs with the
 * rule it bounds.
 */
export const SENDER_GROUP_WINDOW_MS = 10 * 60 * 1000;

/** One message's place in a run of messages from the same sender. */
export interface MessageGrouping {
  /** Render the sender name + time header above this message. */
  showHeader: boolean;
  /**
   * The DAY SEPARATOR to render above this message, or null (C-Day). Non-null on the first message of each
   * local calendar day, which is why it is decided in the same pass as the run: both answers are "what does
   * the message ABOVE this one say", and a component asking each row to look backwards on its own is the
   * same derivation written N times.
   */
  daySeparator: string | null;
}

/**
 * Decide, for each message in READING ORDER, whether it opens a new run and therefore wears a header.
 *
 * A message continues the run above it when the sender is the same AND it falls inside
 * {@link SENDER_GROUP_WINDOW_MS} of the run's FIRST message. Otherwise it starts a new run: the header comes
 * back and the window restarts from this message.
 *
 * **A pure function over the served rows, deliberately.** It is a DISPLAYED RULE, and the thread view is not
 * the only surface that will ever render a conversation — computing it inline in the component would put a
 * second copy one screen away the moment mobile grows its own. It also makes the awkward cases assertable
 * without a DOM: an unparseable timestamp, a sender the row cannot name, and the boundary itself.
 *
 * **Identity is `senderPersonId`** — a sender is always a person now that the band kind is retired
 * (ART-159). A message carrying none is its own run — it cannot be shown to belong with anything.
 */
export function groupMessages(
  messages: readonly { senderPersonId?: string | null; createdOn?: string | null }[],
  now: number = Date.now(),
): MessageGrouping[] {
  let runSender: string | null = null;
  let runStart = 0;
  let lastDay: number | null = null;

  return messages.map((m) => {
    const sender = m.senderPersonId ?? null;
    const at = m.createdOn ? new Date(m.createdOn).getTime() : Number.NaN;

    // THE DAY SEPARATOR, decided before the run — a message that opens a new day always opens a new run
    // too, whoever sent it, because the header under a separator would otherwise be missing from the first
    // message of the day. A row with no readable timestamp belongs to no day and gets no separator rather
    // than being filed under the previous one.
    const day = dayKey(m.createdOn);
    const newDay = day !== null && day !== lastDay;
    if (day !== null) lastDay = day;
    const daySeparator = newDay ? dayLabel(m.createdOn, now) : null;

    // An unreadable or absent timestamp cannot be shown to be inside the window, so it opens a run. Grouping
    // it would hide a sender behind a header that may belong to a different hour.
    const sameSender = sender !== null && sender === runSender;
    const inWindow = Number.isFinite(at) && Number.isFinite(runStart) && at - runStart < SENDER_GROUP_WINDOW_MS;

    if (sameSender && inWindow && !newDay) return { showHeader: false, daySeparator };

    runSender = sender;
    runStart = at;
    return { showHeader: true, daySeparator };
  });
}

/**
 * The start of the LOCAL calendar day a moment falls in.
 *
 * **The ONE expression of "which day is this", shared by the separator's key and its label.** They have to
 * agree or a boundary lands in one place and gets named for another, and the tempting shortcut in the key —
 * `iso.slice(0, 10)` — is the UTC day, which files everything after 7pm Eastern under tomorrow. Sharing the
 * helper is what makes that unavailable rather than merely wrong: there is no second place to slice.
 *
 * *(A test cannot portably prove the locality: a runner in UTC gets the same answer either way, which is
 * the shape `TZ=UTC pnpm test` is otherwise used to FIND. Structure is the guard here, not an assertion.)*
 */
function localDayStart(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** A message's LOCAL calendar day, as a comparable key, or null when the stamp is unreadable. */
function dayKey(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : localDayStart(d);
}

/**
 * The words on a day separator — "Today", "Yesterday", or "Mon, Nov 14" (with the year once it is not this
 * one). The spec's own format is "DAY · TIME"; the TIME half is already on every message header, so
 * repeating it in the separator would put the same minute on screen twice, one line apart.
 */
export function dayLabel(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date(now);
  // The same local-day arithmetic the separator KEY uses, so the boundary and its name cannot disagree.
  const days = Math.round((localDayStart(today) - localDayStart(d)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(d.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** A compact relative age for a thread's last-message time ("now" / "5m" / "3h" / "2d" / "1w" / a date). */
export function relativeTime(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((now - then) / 1000));
  if (secs < 60) return "now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return new Date(then).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Device-local 12h wall-clock for a message bubble (e.g. "3:07 PM"). NEVER shows a trailing `Z` — the ISO is
 * parsed to a Date and formatted in the viewer's timezone. Blank in → "".
 */
export function formatMessageTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true });
}

/** Format an int64-cents money value as a whole-dollar string (04 typed money — cents on the wire). */
export function formatMoneyCents(cents: number | null | undefined): string {
  if (cents == null) return "";
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

/** Format a door-split percentage (double) as e.g. "20%". Blank in → "". */
export function formatDoorSplit(pct: number | null | undefined): string {
  if (pct == null) return "";
  return `${Number.isInteger(pct) ? pct : Number(pct.toFixed(1))}%`;
}

/** Optimistically ADD the caller's reaction to a message's reaction list (mine=true; bump/insert the emoji). */
export function addReaction(reactions: readonly ReactionDto[] | null | undefined, emoji: string): ReactionDto[] {
  const list = reactions ?? [];
  const existing = list.find((r) => r.emoji === emoji);
  if (existing) {
    return list.map((r) => (r.emoji === emoji ? { ...r, count: (r.count ?? 0) + (r.mine ? 0 : 1), mine: true } : r));
  }
  return [...list, { emoji, count: 1, mine: true }];
}

/** Optimistically REMOVE the caller's reaction (mine=false; decrement, dropping the chip at zero). */
export function removeReaction(reactions: readonly ReactionDto[] | null | undefined, emoji: string): ReactionDto[] {
  const list = reactions ?? [];
  return list
    .map((r) => (r.emoji === emoji ? { ...r, count: Math.max(0, (r.count ?? 0) - (r.mine ? 1 : 0)), mine: false } : r))
    .filter((r) => (r.count ?? 0) > 0);
}
